import { useEffect, useRef, useState } from 'react'
import ChevronIcon from '@nv/images/design/chevron-icon'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { FormikError } from '../formik_error/FormikError'
import { Times } from './presets/Times'
import styles from './Select.module.scss'

export function Select({ label, formik, options, field, value, isRequired = false, onChange, ariaLabel, isSearchable = false }: any) {
  const { t } = useTranslation()
  const [displayValue, setDisplayValue] = useState(value ? value.label || value.id : label || 'Select an option')
  const [isDropdownOpen, setIsSelectOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const topicOpRef = useRef(null)
  const placeholder = label ? label : t('form.select.selectOption')
  if (options === 'times') options = Times

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (isDropdownOpen && topicOpRef.current && !topicOpRef.current.contains(e.target)) {
        setIsSelectOpen(false)
      }
    }
    document.addEventListener('mousedown', checkIfClickedOutside)
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside)
    }
  }, [isDropdownOpen])

  useEffect(() => {
    if (value) {
      const selectedOption = options.find((option) => option.value === value)
      if (selectedOption) setDisplayValue(selectedOption.label || selectedOption.id)
    } else {
      setDisplayValue(placeholder)
    }
  }, [value, options, placeholder])

  useEffect(() => {
    isDropdownOpen && topicOpRef.current && topicOpRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [isDropdownOpen])

  const handleSelectOption = (item) => {
    setDisplayValue(item.label || item.id)
    if (formik) formik.setFieldValue(field, item.value)
    if (onChange) onChange(item)

    setIsSelectOpen(false)
  }

  const Options = isSearchable
    ? options.filter((item) => (item.label || item.id).toLowerCase().includes(searchTerm.toLowerCase()))
    : options

  return (
    <div className={styles['container']}>
      <label className={styles['label']}>
        {label} {isRequired && <span className={styles['required']}>*</span>}
      </label>
      <div
        className={clsx(styles['select'], { [styles['error-select']]: formik?.touched[field] && formik?.errors[field] })}
        onClick={() => setIsSelectOpen(!isDropdownOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setIsSelectOpen(!isDropdownOpen)
        }}
        role='button'
        tabIndex={0}
        aria-label={ariaLabel}
        aria-expanded={isDropdownOpen}
      >
        <span className={clsx({ [styles['placeholder']]: displayValue === placeholder })}>{displayValue}</span>
        <ChevronIcon
          className={clsx({
            [styles['chevron-flip']]: isDropdownOpen,
            [styles['chevron']]: !isDropdownOpen,
          })}
        />
      </div>

      {isDropdownOpen && (
        <div className={styles['options']} ref={topicOpRef}>
          {isSearchable && (
            <input
              className={styles['search']}
              placeholder={t('main.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label='Search options'
            />
          )}
          {Options.map((item, index) => (
            <div
              key={index}
              className={styles['option']}
              onClick={() => handleSelectOption(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSelectOption(item)
                }
              }}
              role='button'
              tabIndex={0}
              aria-label={`${ariaLabel || 'Option'}: ${item.label}`}
            >
              <span className={styles['options__text']}>{item.label || item.id}</span>
            </div>
          ))}
        </div>
      )}

      <FormikError formik={formik} fieldName={field} />
    </div>
  )
}
